import { ChangeDetectionStrategy, Component, computed, input, inject, output } from '@angular/core';

import { InterfaceActionButtonComponent } from './interface-action-button.component';
import { EntriesService } from '../../features/admin/modules/features/entries/entries.service';
import { ModulesStore } from '../../features/admin/modules/modules.store';
import type {
  InterfaceAction,
  InterfaceActionDisplayContext,
  InterfaceActionPlacement,
} from '../../features/admin/modules/features/interface-actions/interface-actions.model';
import type { Entry } from '../../features/admin/modules/modules.model';
import { FormGroup } from '@angular/forms';
import { UtilityService } from '../services/utility.service';
import { InterfaceActionsService } from '../../features/admin/modules/features/interface-actions/interface-actions.service';

@Component({
  selector: 'app-module-interface-actions-buttons',
  standalone: true,
  imports: [InterfaceActionButtonComponent],
  template: `
    <div class="flex items-end gap-2" [class.justify-end]="position() === 'end'">
      @for (action of interfaceActions(); track action.id) {
        <app-interface-action-button
          [buttonData]="{
            interfaceAction: action,
            entries: entries(),
          }"
          [context]="context()"
          [buttonSize]="buttonSize()"
          (handleClick)="processAction(action)">
        </app-interface-action-button>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleActionsComponent {
  utilityService = inject(UtilityService);
  // Inputs
  moduleId = input<string>();
  moduleCode = input<string>();
  form = input<FormGroup>();
  entries = input.required<Entry[]>();
  position = input<'start' | 'end'>('end');
  buttonSize = input<'small' | 'medium'>('medium');
  context = input.required<InterfaceActionDisplayContext>(); // New input for context with proper type
  /**
   * Restrict to actions with this placement. Left unset by hosts that ARE the only rendering spot
   * for their context (a standalone button bar), so those keep showing every action regardless of
   * what the admin picked for the paged layout.
   */
  placement = input<InterfaceActionPlacement | undefined>(undefined);

  // Services
  private modulesStore = inject(ModulesStore);
  private entriesService = inject(EntriesService);
  private interfaceActionsService = inject(InterfaceActionsService);

  // Action handler output
  actionTriggered = output<{ action: InterfaceAction; entries: Entry[] }>();

  // Computed values
  private module = computed(() => {
    if (this.moduleId()) {
      return this.modulesStore.modules().find(m => m.id === this.moduleId());
    } else if (this.moduleCode()) {
      return this.modulesStore.modules().find(m => m.code === this.moduleCode());
    }
    return null;
  });

  interfaceActions = computed(() => {
    const allActions = this.module()?.interfaceActions || [];
    // Filter actions based on the context (and placement, when the host asked for a subset)
    return this.interfaceActionsService.filterActionsForContext(allActions, this.context(), this.placement());
  });

  processAction(action: InterfaceAction) {
    const entries = this.entries();

    // Emit the action for parent components to handle if needed
    this.actionTriggered.emit({ action, entries });

    // Only validate form for actions that require it (e.g., editValue actions with forms)
    if (this.shouldValidateForm(action)) {
      if (this.form() && !this.form().valid) {
        this.utilityService.showMessage(
          `Opslaan mislukt. Controleer alle velden nogmaals of zo correct zijn ingevuld.`
        );
        return;
      }
    }

    // Process the action with the entries service
    if (entries.length > 0 && this.module()) {
      for (const entry of entries) {
        this.entriesService.processAction(
          {
            name: 'custom',
            id: entry.id,
            entry: entry,
            interfaceAction: action,
          },
          this.module()
        );
      }
    }
  }

  /**
   * Determine if form validation is required for an action
   * Only certain action types require form validation
   */
  private shouldValidateForm(action: InterfaceAction): boolean {
    const actionType = action.actions?.[0]?.type;

    // Skip validation for actions that don't modify entry data through forms
    // ('navigate' only reads the row-entry id, it never writes form values).
    if (actionType === 'popupInput' || actionType === 'impersonate' || actionType === 'navigate') {
      return false;
    }

    // Skip validation if no form is provided
    if (!this.form()) {
      return false;
    }

    // Validate for editValue actions that work with forms
    if (actionType === 'editValue' && this.form()) {
      return true;
    }

    // Default: don't require validation for unknown action types
    return false;
  }
}
